import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const miniIvf: ContentBlock<"procedure"> = {
  id: makeId("proc-mini-ivf"),
  kind: "procedure",
  tags: ["ivf", "treatment", "her", "during-cycle"],
  title: "Mini-IVF",
  subtitle: "A lower-medication IVF approach using a gentler stimulation plan",
  body: {
    procedureName: "Mini-IVF (mild-stimulation IVF)",
    category: "stimulation",
    description:
      "Mini-IVF, also called mild-stimulation IVF, uses lower doses of fertility medication than a conventional IVF cycle. The goal is usually to retrieve a smaller group of eggs while reducing medication burden; it is not automatically the better fit for every patient.",
    howItWorks:
      "Your clinic may use an oral medication, low-dose injectable medication, or a combination. Ultrasound and blood work track follicle growth, and the clinic adjusts the plan and schedules a trigger when appropriate. Eggs are then retrieved and may be fertilized, cultured, transferred, or frozen using the same lab steps as other IVF cycles.",
    duration:
      "Varies by protocol; your clinic will set the monitoring and retrieval dates",
    anesthesia: "sedation",
    recoveryTime:
      "Most people return to light activity within a day; follow your clinic's guidance",
    risks: [
      "Fewer eggs may be retrieved in a single cycle",
      "More than one cycle may be recommended to build an egg or embryo cohort",
      "Bloating, mood changes, fatigue, or injection-site irritation",
      "Ovarian hyperstimulation syndrome is less common with lower-dose protocols but is not impossible",
      "The egg retrieval has the usual small risks of bleeding, infection, and anesthesia or sedation",
    ],
    prepInstructions: [
      "Ask which medications, doses, and monitoring visits are part of your specific plan",
      "Follow medication timing and storage instructions exactly",
      "Ask what symptoms should prompt a call to the clinic during stimulation",
      "Follow the clinic's fasting and arrival instructions before egg retrieval",
    ],
    whatToExpect: [
      "Baseline testing before medications begin",
      "A shorter or lower-dose stimulation plan than the clinic's conventional IVF protocol",
      "Ultrasound and blood-work monitoring during stimulation",
      "A trigger medication when the clinic determines the follicles are ready",
      "Egg retrieval under the clinic's planned sedation or anesthesia approach",
      "A conversation about fertilization, embryo transfer, freezing, and whether another cycle could be useful",
    ],
    forWhom: "her",
  },
  sources: [
    {
      title:
        "Comparison of pregnancy rates for poor responders using IVF with mild ovarian stimulation versus conventional IVF: a guideline",
      authors:
        "Practice Committee of the American Society for Reproductive Medicine",
      journal: "Fertility and Sterility",
      year: 2018,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/comparison-of-pregnancy-rates-for-poor-responders-using-ivf-with-mild-ovarian-stimulation-versus-conventional-ivf-a-guideline-2018/",
      evidenceLevel: "guideline",
    },
    {
      title: "Side effects of injectable fertility drugs (gonadotropins)",
      authors: "American Society for Reproductive Medicine",
      journal: "ReproductiveFacts.org",
      year: 2024,
      url: "https://www.reproductivefacts.org/news-and-publications/fact-sheets-and-infographics/side-effects-of-injectable-fertility-drugs-gonadotropins/",
      evidenceLevel: "official",
    },
  ],
};

registerBlock(miniIvf);
